import { useState, useEffect, useMemo } from 'react';
import { Box, Container, Paper, Typography, useTheme, Button, Fab, useScrollTrigger, Zoom } from '@mui/material';
import { KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';
import SEO from '../components/SEO';

// Back to top button component
const ScrollTop = ({ children }) => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
      >
        {children}
      </Box>
    </Zoom>
  );
};

const Terms = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const [activeSection, setActiveSection] = useState('');

  // Table of contents wrapped in useMemo
  const sections = useMemo(() => [
    { id: 'services', title: 'Services' },
    { id: 'account', title: 'Account and Access' },
    { id: 'payments', title: 'Payments, Subscriptions, and Refunds' },
    { id: 'service-levels', title: 'Service Levels' },
    { id: 'intellectual-property', title: 'Intellectual Property' },
    { id: 'confidentiality', title: 'Confidentiality' },
    { id: 'liability', title: 'Limitation of Liability' },
    { id: 'termination', title: 'Termination' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'changes', title: 'Changes to Terms' }
  ], []);

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Adjust based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <SEO 
        title="Terms and Conditions | Gravity Point Media" 
        description="Terms and conditions for using Gravity Point Media's website support and maintenance services." 
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'flex-start',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          pt: { xs: 2, sm: 4 },
          pb: { xs: 4, sm: 6 }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Table of Contents */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                height: 'fit-content',
                position: { md: 'sticky' },
                top: { md: 100 }, // Adjust based on header height
                width: { md: 280 },
                display: { xs: 'none', md: 'block' }
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                Table of Contents
              </Typography>
              <Box component="nav">
                {sections.map((section) => (
                  <Button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      color: activeSection === section.id ? theme.palette.primary.main : 'text.secondary',
                      fontWeight: activeSection === section.id ? 600 : 400,
                      mb: 1,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    {section.title}
                  </Button>
                ))}
              </Box>
            </Paper>

            {/* Main Content */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 6 },
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                flex: 1
              }}
            >
              <Box textAlign="center" mb={6}>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #1976d2, #2196f3)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                  }}
                >
                  Terms and Conditions
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Effective Date: {new Date().toLocaleDateString()}
                </Typography>
              </Box>

              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                These Terms and Conditions ("Terms") govern your use of GPM Support ("Service"), a product of Gravity Point Media LLC ("Company," "we," or "us"), located at 155 Willowbrook Blvd, Ste 110, Wayne, New Jersey 07470. By accessing or using our services, you agree to be bound by these Terms.
              </Typography>

              {/* Services Section */}
              <Box id="services" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  1. Services
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  GPM Support provides tier-based website support and development credits to businesses and professionals. Service credits are categorized as:
                </Typography>
                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Support Credits: Used for technical support tickets
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Custom Development Credits: Used for small development tasks
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  Monthly subscription credits reset at the beginning of each billing cycle and do not roll over. One-time and add-on credit purchases do not expire and are prioritized after subscription credits are exhausted. Clients are responsible for understanding the difference between credit types, and misuse may result in credit forfeiture or suspension.
                </Typography>
              </Box>

              {/* Account Section */}
              <Box id="account" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  2. Account and Access
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  You are responsible for maintaining the confidentiality of your account. You agree to provide accurate information and to update it as necessary. Unauthorized sharing of account access, pooling credits across unrelated entities, or abuse of multi-website functionality will result in immediate account review and possible suspension or termination.
                </Typography>
              </Box>

              {/* Payments Section */}
              <Box id="payments" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  3. Payments, Subscriptions, and Refunds
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  All payments are processed via Polar.sh, a Stripe partner. Taxes and VAT are calculated automatically at checkout.
                </Typography>
                <Box component="ul" sx={{ pl: 4, mb: 3 }}>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Subscription charges recur monthly and are non-refundable after processing.
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Refunds are issued on a case-by-case basis if the Company determines, in its sole discretion, that the service materially failed to meet expectations.
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Partial refunds may be granted when partial resolutions are achieved.
                  </Typography>
                  <Typography component="li" variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                    Clients may cancel subscriptions anytime; credits unused at the time of cancellation will not be refunded or carried forward.
                  </Typography>
                </Box>
              </Box>

              {/* Service Levels Section */}
              <Box id="service-levels" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  4. Service Levels
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  Our typical response window is within 1 to 24 hours, depending on support volume and staff availability. While we strive to meet this window, actual resolution time may vary and is not guaranteed. GPM Support is not liable for any losses incurred due to delayed responses.
                </Typography>
              </Box>

              {/* Intellectual Property Section */}
              <Box id="intellectual-property" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  5. Intellectual Property
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  All custom code, design, and assets created during your service period become your property upon full payment. We retain ownership of any tools, frameworks, libraries, and internal systems used to deliver the services.
                </Typography>
              </Box>

              {/* Confidentiality Section */}
              <Box id="confidentiality" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  6. Confidentiality
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  Both parties agree to maintain the confidentiality of proprietary information, including but not limited to website credentials, business strategies, and client data. We will never share credentials or confidential data with third parties except as required to deliver the services.
                </Typography>
              </Box>

              {/* Liability Section */}
              <Box id="liability" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  7. Limitation of Liability
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  To the fullest extent permitted by law, Gravity Point Media LLC shall not be liable for any indirect, incidental, or consequential damages arising from the use or inability to use our services. Our total liability for any claim is limited to the amount paid by the client in the 30 days preceding such a claim. Use of our services is at your own risk.
                </Typography>
              </Box>

              {/* Termination Section */}
              <Box id="termination" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  8. Termination
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We reserve the right to terminate or suspend services immediately for any of the following reasons: non-payment, abuse of services, fraudulent activity, or violation of these Terms. Upon termination, any unused subscription credits are forfeited.
                </Typography>
              </Box>

              {/* Governing Law Section */}
              <Box id="governing-law" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  9. Governing Law
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  These Terms are governed by and construed in accordance with the laws of the State of New Jersey, United States. Any legal actions or disputes shall be filed in the appropriate courts of New Jersey.
                </Typography>
              </Box>

              {/* Changes Section */}
              <Box id="changes" sx={{ scrollMarginTop: '100px' }}>
                <Typography variant="h5" sx={{ mt: 6, mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                  10. Changes to Terms
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                  We may update these Terms periodically. We will notify clients via email or dashboard alerts of material changes. Continued use of the service after changes constitutes acceptance of the new Terms.
                </Typography>
              </Box>

              <Box mt={6} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  © {currentYear} Gravity Point Media LLC. All rights reserved.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Back to Top Button */}
      <ScrollTop>
        <Fab color="primary" size="small" aria-label="scroll back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </>
  );
};

export default Terms; 